import {
    Dialog,
    DialogTrigger,
    DialogSurface,
    DialogTitle,
    DialogBody,
    DialogActions,
    DialogContent,
    RadioGroup,
    Radio,
} from "@fluentui/react-components";
import styled from "styled-components";
import {
    Text,
    Button as ButtonNS,
    Flex,
    FilesPdfIcon,
    ExcelIcon,
} from "@fluentui/react-northstar";
import { useState } from "react";
import { downloadFile } from "../../../../api/downloadFile";
import { toast } from "sonner";

const DialogExportFactsheet = ({
    id_user,
    buttonContent = "⬇ Exporter la fiche",
}) => {
    const [typeFileSelected, setTypeFileSelected] = useState("pdf");
    const [isLoading, setIsLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);

    const exportFactsheet = async () => {
        setIsLoading(true);
        const result = await downloadFile({
            url: `/user/${id_user}/identity/export/${typeFileSelected}`,
            fallbackFilename: `fiche_engagement_${id_user}.${typeFileSelected}`,
        });

        if (!result.success) {
            setIsLoading(false);
            return toast.error(
                result.message ||
                    "Une erreur est survenue lors du téléchargement.",
                {
                    position: "bottom-right",
                }
            );
        }
        setIsLoading(false);
        toast.success(
            `Le fichier "${result.filename}" a été téléchargé avec succès.`,
            {
                position: "bottom-right",
            }
        );
        resetStates();
        return;
    };
    const onChangeRadioSelection = (value) => {
        setTypeFileSelected(value);
        return;
    };
    const resetStates = () => {
        setTypeFileSelected("pdf");
        setIsLoading(false);
        setOpenDialog(false);
        return;
    };

    return (
        <Dialog
            open={openDialog}
            onOpenChange={(_, data) => setOpenDialog(data.open)}
        >
            <DialogTrigger>
                <SButtonExport flat content={buttonContent} />
            </DialogTrigger>
            <DialogSurface>
                <DialogBody>
                    <DialogTitle>
                        <STextTitle
                            content={"⬇ Exporter la fiche d'engagement"}
                        />
                    </DialogTitle>
                    <DialogContent style={{ paddingBottom: 25 }}>
                        <Flex column gap="gap.medium">
                            <STextDescription
                                content={`Sélectionnez le format d'export. La fiche contiendra 
                                toutes les informations saisies par les RH et par le collaborateur. Les données confidentielles 
                                (salaire, IBAN) seront incluses uniquement si vous disposez des droits d'accès paie.`}
                            />
                            <Flex>
                                <SRadioGroup
                                    onChange={(e, { value }) =>
                                        onChangeRadioSelection(value)
                                    }
                                >
                                    <SRadio
                                        value="pdf"
                                        className={
                                            typeFileSelected === "pdf"
                                                ? "checked"
                                                : ""
                                        }
                                        defaultChecked={
                                            typeFileSelected === "pdf"
                                        }
                                        label={
                                            <SFlexLabel
                                                gap="gap.small"
                                                vAlign="center"
                                            >
                                                <FilesPdfIcon size="larger" />
                                                <Flex column>
                                                    <TextRadioTitle content="PDF — Fiche d'engagement complète" />
                                                    <TextRadioDescription content="Format recommandé pour archivage et signature" />
                                                </Flex>
                                            </SFlexLabel>
                                        }
                                    />
                                    <SRadio
                                        value="xlsx"
                                        className={
                                            typeFileSelected === "xlsx"
                                                ? "checked"
                                                : ""
                                        }
                                        defaultChecked={
                                            typeFileSelected === "xlsx"
                                        }
                                        label={
                                            <SFlexLabel
                                                gap="gap.small"
                                                vAlign="center"
                                            >
                                                <ExcelIcon size="larger" />
                                                <Flex column>
                                                    <TextRadioTitle content="Excel (.xlsx) — Fiche modifiable" />
                                                    <TextRadioDescription content="Format éditable pour ajustements avant impression" />
                                                </Flex>
                                            </SFlexLabel>
                                        }
                                    />
                                </SRadioGroup>
                            </Flex>
                        </Flex>
                    </DialogContent>
                    <DialogActions>
                        <DialogTrigger disableButtonEnhancement>
                            <ButtonNS
                                secondary
                                flat
                                content={"Annuler"}
                                onClick={resetStates}
                            />
                        </DialogTrigger>
                        <ButtonNS
                            primary
                            flat
                            loading={isLoading}
                            onClick={exportFactsheet}
                            content={"Exporter"}
                        />
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};

export default DialogExportFactsheet;

const SFlexLabel = styled(Flex)`
    padding: 6px 7px;
`;
const SRadioGroup = styled(RadioGroup)`
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
`;
const SRadio = styled(Radio)`
    gap: 4px;
    border: 1px solid var(--border);
    border-radius: var(--radius);
    cursor: pointer;
    transition: all 0.12s;
    align-items: center;
    width: 100%;
    label {
        width: 100%;
        padding: 0;
    }
    &:hover,
    &.checked {
        border-color: var(--purple);
        background: var(--purple-light);
    }
`;
const TextRadioDescription = styled(Text)`
    font-size: 11px;
    color: var(--text-tert);
`;
const TextRadioTitle = styled(Text)`
    font-size: 13px;
    font-weight: 600;
`;
const STextTitle = styled(Text)`
    font-size: 17px;
    font-weight: 700;
    margin-bottom: 6px;
`;
const STextDescription = styled(Text)`
    font-size: 13px;
    color: var(--text-sec);
    margin-bottom: 20px;
    line-height: 1.6;
`;
const SButtonExport = styled(ButtonNS)`
    background: var(--bg-card);
    color: var(--purple);
    border: 1.5px solid var(--purple);
`;
